import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as microsoftTeams from "@microsoft/teams-js";
import { SCOPE_GET_GRAPHCLIENT, SCOPE_INIT } from "./ScopesReducer.interfaces";
import { TeamsUserCredential } from "@microsoft/teamsfx";
import { checkConfig } from "../../../services/ScopesService/ScopesService";
import { login } from "../AuthReducer/AuthReducer";
import { ErrorModule } from "../../../components/ErrorBoundary/ErrorBoundary";
import { getSliceSelector } from "../../utils";
import { Client } from "@microsoft/microsoft-graph-client";
import { TokenApi } from "../../../apis/Token/TokenApi";
export let teamsFxGraphClient = undefined;
export let scopesServiceButtonAction = undefined;
const initialState = {
    loaded: false,
    needToConsent: false,
    isConsenting: false,
    isFinishing: false,
    consentFailed: false,
    teamsFxConfig: undefined,
};
// A fresh consent takes a few seconds to reach the token endpoint: poll it instead of
// waiting a fixed delay, and give up (reload anyway) after the same bound as before.
const CONSENT_PROPAGATION_TIMEOUT_MS = 15000;
const CONSENT_PROPAGATION_POLL_MS = 2000;
const waitForGraphToken = async (scopes) => {
    const deadline = Date.now() + CONSENT_PROPAGATION_TIMEOUT_MS;
    while (Date.now() < deadline) {
        if (await TokenApi.getGraphToken(scopes))
            return;
        await new Promise(r => setTimeout(r, CONSENT_PROPAGATION_POLL_MS));
    }
};
const slice = createSlice({
    name: "scopes",
    initialState,
    reducers: {
        setScope: (state, action) => {
            Object.assign(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initializeScope.fulfilled, (state, action) => {
            if (!action.payload)
                return ErrorModule.showErrorAlert("Can't initialize scopes reducer");
            Object.assign(state, action.payload);
        });
        builder.addCase(getGraphClient.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            Object.assign(state, action.payload);
        });
    }
});
export const initializeScope = createAsyncThunk(SCOPE_INIT, async (data, { dispatch }) => {
    const { initArgs } = data;
    checkConfig(initArgs);
    const scopes = initArgs.scopes?.map(s => "https://graph.microsoft.com/" + s) ?? [];
    const teamsFxConfig = {
        initiateLoginEndpoint: initArgs.apiBaseUrl + "/auth-start.html",
        apiEndpoint: initArgs.apiBaseUrl + "",
        clientId: initArgs.clientId + "",
        tenantId: initArgs.tenantId + "",
        scopes: scopes.join(" "),
        authorityHost: "https://login.microsoftonline.com/common/v2.0",
        applicationIdUri: initArgs.apiBaseUrl + "/auth-start.html",
        loginPageTitle: initArgs.loginPageTitle,
        loginPageSubtitle: initArgs.loginPageSubtitle,
    };
    const teamsFxCredential = new TeamsUserCredential(teamsFxConfig);
    if (!data.askGraphToken)
        return { loaded: true, needToConsent: false, isConsenting: false };
    dispatch(getGraphClient({ teamsFxCredential, scopes, initArgs }));
    return { teamsFxConfig };
});
export const initGraphClient = async (scopes) => {
    const graphToken = await TokenApi.getGraphToken(scopes);
    if (!graphToken)
        throw new Error("Invalid graph token");
    teamsFxGraphClient = Client.init({ authProvider: (callback) => callback(undefined, graphToken) });
    return { loaded: true, needToConsent: false, isConsenting: false };
};
const manageRedirect = (getState) => {
    const infos = getState().draft.infos;
    if (infos) {
        window.location.assign(window.location.origin + window.location.pathname + "?draft=" + encodeURIComponent(JSON.stringify(infos)));
    }
    else {
        window.location.reload();
    }
};
export const getGraphClient = createAsyncThunk(SCOPE_GET_GRAPHCLIENT, async (data, { getState, dispatch }) => {
    const { teamsFxCredential, scopes, initArgs } = data;
    try {
        return await initGraphClient(initArgs.scopes.join());
    }
    catch (error) {
        try {
            if (initArgs.isOnMobile && initArgs.inTeams) {
                await teamsFxCredential.login(scopes);
                window.location.reload();
            }
            else {
                scopesServiceButtonAction = async () => {
                    dispatch(setScope({ isConsenting: true, consentFailed: false }));
                    if (!initArgs.inTeams) {
                        dispatch(login());
                        setTimeout(() => manageRedirect(getState), 10000);
                        return;
                    }
                    const graphScopes = initArgs.scopes.join();
                    try {
                        await teamsFxCredential.login(scopes);
                    }
                    catch {
                        // A closed window and an "admin approval required" page both land here.
                        // Stay on the screen so the user can retry or reach an admin, unless the
                        // consent went through anyway.
                        if (await TokenApi.getGraphToken(graphScopes))
                            return manageRedirect(getState);
                        dispatch(setScope({ isConsenting: false, consentFailed: true }));
                        return;
                    }
                    dispatch(setScope({ isConsenting: false, isFinishing: true }));
                    await waitForGraphToken(graphScopes);
                    manageRedirect(getState);
                };
                dispatch(setScope({ needToConsent: true }));
                microsoftTeams.app.notifySuccess();
            }
        }
        catch (loginError) {
            window.location.reload();
        }
        return;
    }
});
export const { setScope } = slice.actions;
export const useScopesSelector = getSliceSelector(slice);
export const scopesReducer = slice.reducer;
